/**
 * The quick look at one video: the whole video playing on the left with its transcript under it,
 * its best clips as pictures on the right. A calm first view before the full sheet (the ET), which
 * stays one click away.
 *
 * - Clicking a clip plays just that clip (it stops at the clip's end); the big player's own
 *   controls play the whole video.
 * - "Every clip, in order" (the default) is the whole video as clips, one per sentence, and it
 *   follows the player: the clip being said is lit and kept in view, like a transcript. The
 *   other chips (Longest quotes, All positive…) are shortlists.
 * - ⬇ on a clip makes it a video file (captions burned in) and then offers Download. Ticking clips
 *   picks them; the bar at the bottom plays the picks in a row, stitches them into one video, or
 *   makes each its own file.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Virtuoso, VirtuosoGrid, VirtuosoGridHandle, VirtuosoHandle } from "react-virtuoso";
import { VideoData } from "../types";
import { formatDateOnly } from "../../../../utils/dateFormatting";
import { useFileDownload } from "../hooks/useFileDownload";
import Pic from "./Pic";
import { CARD, MORE_WAYS, QUICK } from "./libraryLookText";
import { ROUTES } from "../../../../constants";
import AiWays from "./AiWays";
import ArtisticRequest from "../../../studio/ArtisticRequest";
import LinkIcon from "@mui/icons-material/Link";
import { clock, ReelClip, useTopClips } from "./topClips";
import { Job, useClipMaker } from "./clipMaker";
import { lineAt, useClipWords, useTranscriptLines } from "./transcriptLines";
import cherry from "../../../../assets/brand/logo_site_onlight.svg";

const LIMIT = 50;
/** The chip for the whole video as clips, in order. */
const ALL = "all";

interface Props {
  video: VideoData;
  /** Start here (seconds), e.g. from a "what was said" result. */
  startAt?: number;
  /** Start by playing this clip (its id), e.g. a moment clicked on a Strips row. */
  startClipId?: number;
  onBack: () => void;
  onOpenClips: (video: VideoData) => void;
  onTop20: (video: VideoData) => void;
  renderMenu: (video: VideoData) => React.ReactNode;
  /** Make a share link to the full sheet and copy it (the same as ⋯ → Share). */
  onShare: (videoId: number) => void;
  /** Open the full sheet with Black Cherry (the AI panel) already open. */
  onOpenBlackCherry: (video: VideoData) => void;
  /** Open the full sheet with these clips picked, in this order, and its exporter open. */
  onOpenExporter: (video: VideoData, clipIds: number[]) => void;
  /** Open the artistic-edit form at once (the ready email's link: ?art=1). */
  startArt?: boolean;
}

const JobButton: React.FC<{ job?: Job; label: string; onMake: () => void; onDownload: (url: string) => void; small?: boolean }> = ({
  job, label, onMake, onDownload, small,
}) => {
  const cls = small ? "dl" : "pill red";
  if (job?.state === "making") return <button type="button" className={`${cls} busy`} disabled title={QUICK.makingTitle}>{small ? "…" : QUICK.making}</button>;
  if (job?.state === "ready" && job.url) {
    const url = job.url;
    return <button type="button" className={`${cls} ready`} title={QUICK.downloadTitle} onClick={(e) => { e.stopPropagation(); onDownload(url); }}>{small ? "⬇" : QUICK.download}</button>;
  }
  return (
    <button type="button" className={cls} title={job?.state === "failed" ? QUICK.failed : QUICK.makeTitle} onClick={(e) => { e.stopPropagation(); onMake(); }}>
      {job?.state === "failed" ? (small ? "!" : QUICK.tryAgain) : label}
    </button>
  );
};

const QuickView: React.FC<Props> = ({ video, startAt, startClipId, onBack, onOpenClips, onTop20, renderMenu, onShare, onOpenBlackCherry, onOpenExporter, startArt }) => {
  const [aiOpen, setAiOpen] = useState(false);
  const [recipe, setRecipe] = useState(ALL);
  // Cards (side by side, the default) or a list; remembered in this browser.
  const [layout, setLayoutState] = useState<"cards" | "list">(() => {
    try { return localStorage.getItem("cc_qv_layout") === "list" ? "list" : "cards"; } catch { return "cards"; }
  });
  const setLayout = (next: "cards" | "list") => {
    setLayoutState(next);
    try { localStorage.setItem("cc_qv_layout", next); } catch { /* ignore */ }
  };
  // The shortlist call also brings the chips' names, so it runs for "all" too (one small answer).
  // What the person typed in the search box, and the size of the words under the clips (remembered).
  const [query, setQuery] = useState("");
  const [bigText, setBigTextState] = useState(() => {
    try { return localStorage.getItem("cc_qv_text") === "big"; } catch { return false; }
  });
  const setBigText = (big: boolean) => {
    setBigTextState(big);
    try { localStorage.setItem("cc_qv_text", big ? "big" : "normal"); } catch { /* ignore */ }
  };
  const { data, loading, error } = useTopClips(video.id, LIMIT, recipe === ALL ? "longest_quotes" : recipe, true, String(video.thumbnails_ready !== false));
  const lines = useTranscriptLines(video.id);
  // Every sentence as a clip, in the same shape as the shortlist's, so picks/⬇/stitch work the same.
  const allClips: ReelClip[] = useMemo(() => lines.lines.map((l) => ({
    id: l.id, kind: "sentence", text: l.text, label: "", speaker: l.speaker, sentiment: "",
    start_in: l.start, end_in: l.end, seconds: l.end - l.start, thumbnail: l.thumbnail, model: "sentence",
  })), [lines.lines]);
  const { jobs, make, forget } = useClipMaker(video.id);
  const { download } = useFileDownload({ onError: () => { /* the button stays; the person can try again */ } });
  const player = useRef<HTMLVideoElement>(null);
  const stopAt = useRef<number | null>(null);
  const ourSeeks = useRef(0);
  const queue = useRef<ReelClip[]>([]);
  const [playing, setPlaying] = useState<{ id: number | null; start: number; text: string } | null>(null);
  const [now, setNow] = useState(0);
  const [picks, setPicks] = useState<ReelClip[]>([]);
  const total = video.clips_count || 0;
  // Keep the first signed link: the library list is re-fetched (and re-signed) while anything is
  // processing, and a new src would reload the player back to 0:00 mid-clip.
  const src = useRef<{ id: number; url: string } | null>(null);
  if (!src.current || src.current.id !== video.id) src.current = { id: video.id, url: video.video || "" };

  const play = useCallback((start: number, end: number | null, id: number | null, text: string) => {
    const el = player.current;
    if (!el) return;
    stopAt.current = end;
    setPlaying({ id, start, text });
    const go = () => {
      ourSeeks.current += 1;
      el.currentTime = start;
      el.play().catch(() => { /* the person presses play */ });
    };
    if (el.readyState >= 1) go();
    else el.addEventListener("loadedmetadata", go, { once: true });
  }, []);

  const playClip = useCallback((clip: ReelClip) => play(clip.start_in, clip.end_in, clip.id, clip.text || clip.label), [play]);

  // A "what was said" result opens here at its line.
  useEffect(() => {
    if (typeof startAt === "number" && startAt > 0 && !startClipId) play(startAt, null, null, "");
  }, [video.id, startAt, startClipId, play]);

  // A moment clicked on a Strips row plays as soon as its clip is here.
  const startedClip = useRef(false);
  useEffect(() => {
    if (!startClipId || startedClip.current) return;
    const clip = allClips.find((c) => c.id === startClipId) || data?.clips.find((c) => c.id === startClipId);
    if (clip) { startedClip.current = true; playClip(clip); }
  }, [startClipId, data, allClips, playClip]);

  useEffect(() => {
    const el = player.current;
    if (!el) return undefined;
    const tick = () => {
      setNow(el.currentTime);
      if (stopAt.current !== null && el.currentTime >= stopAt.current) {
        stopAt.current = null;
        const next = queue.current.shift();
        if (next) playClip(next);
        else el.pause();
      }
    };
    // Moving the big player by hand means "the whole video" again.
    const seekByHand = () => {
      if (ourSeeks.current > 0) { ourSeeks.current -= 1; return; }
      stopAt.current = null;
      queue.current = [];
      setPlaying(null);
    };
    el.addEventListener("timeupdate", tick);
    el.addEventListener("seeking", seekByHand);
    return () => { el.removeEventListener("timeupdate", tick); el.removeEventListener("seeking", seekByHand); };
  }, [playClip]);

  // The clip being said right now, and the list keeping it in view (unless the person is scrolling it).
  const list = useRef<VirtuosoHandle>(null);
  const cardsGrid = useRef<VirtuosoGridHandle>(null);
  const handScroll = useRef(0);
  const nowId = recipe === ALL
    ? lineAt(lines.lines, now)?.id
    : (data?.clips || []).find((c) => c.start_in <= now && now < c.end_in)?.id;
  const find = query.trim().toLowerCase();
  const says = useCallback((c: ReelClip) => !find || (c.text || c.label || "").toLowerCase().includes(find), [find]);
  const allShown = useMemo(() => (find ? allClips.filter(says) : allClips), [allClips, find, says]);
  const shown = recipe === ALL ? allShown : (data?.clips || []).filter(says);
  // A Super Cut always draws on every sentence that says it, whichever chip is on.
  const superClips = find ? allShown : [];
  const superKey = `supercut:${find}`;
  // The words of the clip being said, lit one by one as they're spoken.
  const saying = shown.find((c) => c.id === nowId);
  const words = useClipWords(video.id, saying ? { start: saying.start_in, end: saying.end_in } : null);
  useEffect(() => {
    if (!nowId || Date.now() - handScroll.current < 4000) return;
    const index = shown.findIndex((c) => c.id === nowId);
    if (index < 0) return;
    if (recipe === ALL && layout === "list") list.current?.scrollToIndex({ index, align: "center", behavior: "smooth" });
    else if (recipe === ALL) cardsGrid.current?.scrollToIndex({ index, align: "center", behavior: "smooth" });
    else document.getElementById(`qv-clip-${nowId}`)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [nowId]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePick = (clip: ReelClip) => {
    if (jobs.stitch?.state !== "making") forget("stitch");
    setPicks((p) => (p.some((c) => c.id === clip.id) ? p.filter((c) => c.id !== clip.id) : [...p, clip]));
  };
  const pickNumber = (clip: ReelClip) => picks.findIndex((c) => c.id === clip.id) + 1;
  const oneKind = picks.every((c) => c.model === picks[0]?.model);
  const clipTitle = (clip: ReelClip) => `${video.title} — ${(clip.text || clip.label).slice(0, 40)}`.trim();
  const save = (url: string, name: string) => download(video.id, url, name, "mp4");

  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const move = (from: number, to: number) => {
    if (from === to || to < 0 || to >= picks.length) return;
    if (jobs.stitch?.state !== "making") forget("stitch");
    setPicks((p) => { const n = [...p]; const [c] = n.splice(from, 1); n.splice(to, 0, c); return n; });
  };
  const basketSeconds = picks.reduce((sum, c) => sum + Math.max(0, c.end_in - c.start_in), 0);

  const playPicks = () => {
    if (!picks.length) return;
    const [first, ...rest] = picks;
    queue.current = rest;
    playClip(first);
  };


  // The searched words, marked in a clip's line.
  const lit = (text: string) => {
    if (!find) return text;
    const parts = text.split(new RegExp(`(${find.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "i"));
    return parts.map((part, i) => (i % 2 ? <mark key={i}>{part}</mark> : part));
  };

  // One clip: picture (time, + to pick, ⬇ to download) and its line. `row` = the in-order list.
  const card = (clip: ReelClip, row: boolean) => {
    const n = pickNumber(clip);
    return (
      <div
        key={`${clip.kind}-${clip.id}`}
        id={`qv-clip-${clip.id}`}
        className={`${row ? "qrow" : "mom"}${playing?.id === clip.id || nowId === clip.id ? " on" : ""}${n ? " picked" : ""}`}
        onClick={() => playClip(clip)}
        title={clip.text}
      >
        <Pic src={clip.thumbnail}>
          <span className="t">{clock(clip.start_in)}</span>
          <button
            type="button"
            className={`pick${n ? " on" : ""}`}
            aria-label={n ? QUICK.unpick : QUICK.pick}
            title={n ? QUICK.unpick : QUICK.pick}
            onClick={(e) => { e.stopPropagation(); togglePick(clip); }}
          >
            {n || "+"}
          </button>
          <JobButton
            small
            job={jobs[String(clip.id)]}
            label="⬇"
            onMake={() => make(String(clip.id), [clip], clipTitle(clip))}
            onDownload={(url) => save(url, clipTitle(clip))}
          />
        </Pic>
        <div className="txt">
          {clip.speaker ? <div className="who">{clip.speaker}</div> : null}
          <p>
            {clip.id === nowId && words.length
              ? words.map(([w, start], i) => <span key={i} className={start <= now ? "w said" : "w"}>{w} </span>)
              : row ? lit(clip.text || clip.label) : <>“{lit(clip.text || clip.label)}”</>}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="top" style={{ marginTop: 6 }}>
        <button type="button" className="pill line" onClick={onBack}>{QUICK.back}</button>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div className="ttl" style={{ fontSize: 22 }}>{video.title}</div>
          <div className="meta">
            {formatDateOnly(video.created_at)}
            {video.duration_in_seconds ? <> · {clock(video.duration_in_seconds)}</> : null}
            {total ? <> · <b>{CARD.clips(total)}</b></> : null}
          </div>
        </div>
        <button type="button" className="pill line" onClick={() => onTop20(video)}>{QUICK.top20}</button>
        <div>{renderMenu(video)}</div>
      </div>

      <div className={`qv${bigText ? " bigtext" : ""}`}>
        <div className="player">
          <video ref={player} className="big" src={src.current.url || undefined} poster={video.thumbnail || undefined} controls playsInline preload="metadata" />
          <div className="now">
            {playing ? (
              <>{QUICK.playing} <b>{clock(playing.start)}</b>{playing.text ? <> · “{playing.text}”</> : null}</>
            ) : (
              QUICK.playHint
            )}
          </div>

          {picks.length > 0 ? (
            <div className="basket">
              <div className="b-hd">
                <img src={cherry} alt="" className="b-logo" />
                <b>{QUICK.basket}</b>
                <span className="meta">{QUICK.picked(picks.length)} · {clock(basketSeconds)}</span>
                <button type="button" className="x" title={QUICK.clear} aria-label={QUICK.clear}
                  onClick={() => { setPicks([]); if (jobs.stitch?.state !== "making") forget("stitch"); }}>✕</button>
              </div>
              <div className="b-line">
                {picks.map((clip, i) => (
                  <div
                    key={clip.id}
                    className={`b-item${playing?.id === clip.id ? " on" : ""}`}
                    style={{ width: Math.min(260, Math.max(132, (clip.end_in - clip.start_in) * 10)) }}
                    draggable
                    onDragStart={() => setDragFrom(i)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => { if (dragFrom !== null) move(dragFrom, i); setDragFrom(null); }}
                    onClick={() => playClip(clip)}
                    title={clip.text}
                  >
                    <Pic src={clip.thumbnail}>
                      <span className="n">{i + 1}</span>
                      <span className="t">{clock(clip.end_in - clip.start_in)}</span>
                      <button type="button" className="rm" aria-label={QUICK.unpick} onClick={(e) => { e.stopPropagation(); togglePick(clip); }}>×</button>
                    </Pic>
                    <div className="b-txt">{clip.text || clip.label}</div>
                    <div className="b-move">
                      <button type="button" aria-label={QUICK.moveLeft} onClick={(e) => { e.stopPropagation(); move(i, i - 1); }}>◀</button>
                      <button type="button" aria-label={QUICK.moveRight} onClick={(e) => { e.stopPropagation(); move(i, i + 1); }}>▶</button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="b-act">
                <button type="button" className="pill line" onClick={playPicks}>▶ {QUICK.playPicks}</button>
                {picks.length > 1 && (oneKind ? (
                  <JobButton
                    job={jobs.stitch}
                    label={`✂ ${QUICK.stitch}`}
                    onMake={() => make("stitch", picks, `${video.title} — ${QUICK.stitched(picks.length)}`)}
                    onDownload={(url) => save(url, `${video.title} — ${QUICK.stitched(picks.length)}`)}
                  />
                ) : <span className="meta">{QUICK.oneList}</span>)}
                {picks.length > 1 && (
                  <button type="button" className="pill line" title={QUICK.eachTitle} onClick={() => picks.forEach((c) => make(String(c.id), [c], clipTitle(c)))}>
                    ⬇ {QUICK.each}
                  </button>
                )}
                {picks.every((c) => c.model === "sentence") && (
                  <button type="button" className="pill line" title={QUICK.exporterTitle} onClick={() => onOpenExporter(video, picks.map((c) => c.id))}>
                    {QUICK.exporter} →
                  </button>
                )}
              </div>
              <div className="meta" style={{ marginTop: 6 }}>{QUICK.basketHint}</div>
            </div>
          ) : (
            <div className="basket empty"><img src={cherry} alt="" className="b-logo" />{QUICK.basketEmpty}</div>
          )}

          <div className="open">
            <div className="tx">
              <b>{QUICK.openSheet}</b>
              {QUICK.openSheetNote}
            </div>
            <button type="button" className="pill red" onClick={() => onOpenClips(video)}>{QUICK.openSheet} →</button>
          </div>

          {/* Sharing, on its own: always open. */}
          <div className="sharecard">
              <button type="button" className="way" onClick={() => onShare(video.id)}>
              <span className="way-ic"><LinkIcon sx={{ fontSize: 24, color: "#e61d50" }} /></span>
              <span className="way-tx"><b>{MORE_WAYS.share}</b>{MORE_WAYS.shareNote}</span>
              <span className="way-go">{MORE_WAYS.shareGo}</span>
            </button>
          </div>

          {/* Artistic edits (beta): ask for one. */}
          <ArtisticRequest transcriptId={video.id} startOpen={startArt} />

          {/* The other ways to work with this video, each one line. */}
          <div className="ways">
            <div className="ways-hd">{MORE_WAYS.title}</div>
            <button type="button" className="way" aria-expanded={aiOpen} onClick={() => setAiOpen((o) => !o)}>
              <span className="way-ic">
                <img src="/email/chatgpt.png" alt="ChatGPT" /><img src="/email/claude.png" alt="Claude" /><img src="/email/gemini.png" alt="Gemini" />
              </span>
              <span className="way-tx"><b>{MORE_WAYS.ai}</b>{MORE_WAYS.aiNote}</span>
              <span className="way-go">{aiOpen ? `${MORE_WAYS.close} ▴` : `${MORE_WAYS.aiGo} ▾`}</span>
            </button>
            {aiOpen && <AiWays onOpenBlackCherry={() => onOpenBlackCherry(video)} />}
            <a className="way" href={ROUTES.PLUGIN} target="_blank" rel="noopener noreferrer">
              <span className="way-ic"><span className="pr">Pr</span></span>
              <span className="way-tx"><b>{MORE_WAYS.premiere}</b>{MORE_WAYS.premiereNote}</span>
              <span className="way-go">{MORE_WAYS.premiereGo} →</span>
            </a>
            <a className="way" href={ROUTES.MY_LIBRARY_MEETINGS}>
              <span className="way-ic"><img src="/media/brand/google-meet.svg" alt="Google Meet" style={{ width: 26, height: 22 }} /></span>
              <span className="way-tx"><b>{MORE_WAYS.meetings}</b>{MORE_WAYS.meetingsNote}</span>
              <span className="way-go">{MORE_WAYS.meetingsGo} →</span>
            </a>
            <div className="way">
              <span className="way-ic">
                <a href={MORE_WAYS.appStoreUrl} target="_blank" rel="noopener noreferrer" title="App Store"><img src="/email/app-store.png" alt="App Store" /></a>
                <a href={MORE_WAYS.playUrl} target="_blank" rel="noopener noreferrer" title="Google Play"><img src="/email/google-play.png" alt="Google Play" /></a>
              </span>
              <span className="way-tx"><b>{MORE_WAYS.phone}</b>{MORE_WAYS.phoneNote}</span>
              <a className="way-go" href={ROUTES.APP} target="_blank" rel="noopener noreferrer">{MORE_WAYS.phoneGo} →</a>
            </div>
          </div>
        </div>

        <div>
          <div className="qv-hd">
            <div className="ttl" style={{ fontSize: 17, flex: 1 }}>{QUICK.clipsTitle}</div>
            <div className="ico">
              <button type="button" className={layout === "cards" ? "on" : ""} onClick={() => setLayout("cards")}>▦ {QUICK.cards}</button>
              <button type="button" className={layout === "list" ? "on" : ""} onClick={() => setLayout("list")}>☰ {QUICK.list}</button>
            </div>
          </div>
          {/* The three things to do with a clip, drawn like the buttons on the cards. */}
          <div className="legend">
            <span><i className="lg-play">▶</i>{QUICK.legendPlay}</span>
            <span><i className="lg-pick">+</i>{QUICK.legendPick}</span>
            <span><i className="lg-dl">⬇</i>{QUICK.legendDownload}</span>
          </div>
          <div className="qv-find">
            <div className="sbox">
              <input type="search" value={query} placeholder={QUICK.find} onChange={(e) => setQuery(e.target.value)} />
              {query && <button type="button" aria-label={QUICK.findClear} title={QUICK.findClear} onClick={() => setQuery("")}>×</button>}
            </div>
            <div className="ico">
              <button type="button" title={QUICK.textNormalTitle} className={bigText ? "" : "on"} onClick={() => setBigText(false)}>{QUICK.textNormal}</button>
              <button type="button" title={QUICK.textBigTitle} className={bigText ? "on" : ""} onClick={() => setBigText(true)}><b>{QUICK.textBig}</b></button>
            </div>
          </div>
          <div className="recipes">
            <button type="button" title={QUICK.allTitle} className={`chip${recipe === ALL ? " on" : ""}`} onClick={() => setRecipe(ALL)}>
              {QUICK.all}
            </button>
            {(data?.recipes || []).map((r) => (
              <button type="button" key={r.key} title={r.blurb} className={`chip${recipe === r.key ? " on" : ""}`} onClick={() => setRecipe(r.key)}>
                {r.label}
              </button>
            ))}
          </div>
          {/* Super Cut: the searched words, cut out of every sentence that says them, as one video. */}
          {find && superClips.length > 0 && (
            <div className="supercut">
              <JobButton
                job={jobs[superKey]}
                label={QUICK.superCut(query.trim(), superClips.length)}
                onMake={() => make(superKey, superClips, `${video.title} — Super Cut “${query.trim()}”`, query.trim())}
                onDownload={(url) => save(url, `${video.title} — Super Cut ${query.trim()}`)}
              />
              {jobs[superKey]?.state !== "making" && <span className="meta">{QUICK.superCutTitle}</span>}
            </div>
          )}
          {error && <div className="meta">{QUICK.error}</div>}
          {find && !shown.length && !(recipe === ALL ? lines.loading : loading) && <div className="meta" style={{ padding: "12px 2px" }}>{QUICK.noMatch(query.trim())}</div>}
          {recipe === ALL ? (
            <div className="qlist" onWheel={() => { handScroll.current = Date.now(); }} onTouchMove={() => { handScroll.current = Date.now(); }}>
              {lines.loading && <div className="meta" style={{ padding: 12 }}>{QUICK.loading}</div>}
              {layout === "list" ? (
                <Virtuoso ref={list} data={allShown} style={{ height: "100%" }} itemContent={(_, clip) => card(clip, true)} />
              ) : (
                <VirtuosoGrid
                  ref={cardsGrid}
                  data={allShown}
                  style={{ height: "100%" }}
                  listClassName="qgrid inbox"
                  itemContent={(_, clip) => card(clip, false)}
                />
              )}
            </div>
          ) : layout === "list" ? (
            <div className="qlist auto">{shown.map((clip) => card(clip, true))}</div>
          ) : (
            <div className="qgrid">
              {loading && shown.length === 0
                ? Array.from({ length: 8 }).map((_, i) => <div key={i} className="mom"><div className="pic skel" /><p className="skel" style={{ height: 34, borderRadius: 6 }} /></div>)
                : shown.map((clip) => card(clip, false))}
            </div>
          )}
          {recipe !== ALL && !loading && total > shown.length && (
            <button type="button" className="pill line" style={{ marginTop: 16 }} onClick={() => onOpenClips(video)}>
              {QUICK.seeAll(total)}
            </button>
          )}
        </div>
      </div>

    </div>
  );
};

export default QuickView;
